import { useEffect, useState } from "react";

// Столько же палочек, сколько в живой волне — иначе превью выглядит иначе
const BAR_COUNT = 40;
const PREVIEW_GAMMA = 0.55;

// Кэш по src: одно и то же аудио декодировать на каждый рендер дорого
const cache = new Map();

// Приводим любое количество пиков к фиксированному числу палочек,
// беря максимум в каждом кусочке
export const resample = (source, count) => {
  if (!source || !source.length) return [];
  if (source.length <= count) return source.slice();

  const result = [];
  const chunk = source.length / count;
  for (let i = 0; i < count; i++) {
    const from = Math.floor(i * chunk);
    const to = Math.max(from + 1, Math.floor((i + 1) * chunk));
    let max = 0;
    for (let j = from; j < to; j++) {
      max = Math.max(max, source[j] || 0);
    }
    result.push(max);
  }
  return result;
};

// Нормализация по максимуму + кривая: тихая речь не рисуется плоской линией.
// Считается один раз на пики, поэтому высота палочек не "прыгает" при перемотке
const normalize = (peaks, gamma = PREVIEW_GAMMA) => {
  const max = peaks.reduce((acc, value) => Math.max(acc, value), 0);
  if (max <= 0.001) return peaks;
  return peaks.map((value) => Math.pow(value / max, gamma));
};

const createFakePeaks = (seedKey) => {
  let seed = 0;
  const key = String(seedKey || "voice");
  for (let i = 0; i < key.length; i++) {
    seed = (seed * 31 + key.charCodeAt(i)) % 100000;
  }

  const rand = () => {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    return seed / 2147483648;
  };

  // Речь — это пачки громких участков с паузами, а не белый шум
  const peaks = [];
  let level = 0.35;
  for (let i = 0; i < BAR_COUNT; i++) {
    if (rand() < 0.12) {
      level = 0.1 + rand() * 0.2; // началась пауза / новая фраза
    } else if (rand() < 0.5) {
      level = Math.min(1, Math.max(0.08, level + (rand() - 0.35) * 0.5));
    }
    peaks.push(Math.min(1, Math.max(0.08, level + (rand() - 0.5) * 0.25)));
  }

  return normalize(peaks);
};

const buildPeaks = (audioBuffer) => {
  const channel = audioBuffer.getChannelData(0);
  const blockSize = Math.max(1, Math.floor(channel.length / BAR_COUNT));
  const peaks = [];

  for (let i = 0; i < BAR_COUNT; i++) {
    let max = 0;
    const start = i * blockSize;
    for (let j = 0; j < blockSize; j++) {
      const value = Math.abs(channel[start + j] || 0);
      if (value > max) max = value;
    }
    peaks.push(max);
  }

  return normalize(peaks);
};

/**
 * Пики готовой аудиозаписи в одинаковом виде для обоих сценариев:
 *  - свежая запись: peaks уже есть, нормализуем и приводим к 40 палочкам;
 *  - голосовое в комментарии: декодируем файл и считаем пики сами.
 */
export default function useAudioPeaks(src, providedPeaks) {
  const [peaks, setPeaks] = useState([]);
  const [decodedDuration, setDecodedDuration] = useState(0);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    if (providedPeaks && providedPeaks.length) {
      setPeaks(normalize(resample(providedPeaks, BAR_COUNT)));
      setIsFallback(false);
      return;
    }

    if (!src) {
      setPeaks([]);
      setDecodedDuration(0);
      return;
    }

    if (cache.has(src)) {
      setPeaks(cache.get(src).peaks);
      setIsFallback(false);
      setDecodedDuration(cache.get(src).duration);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(src);
        const buffer = await response.arrayBuffer();
        const AudioContextClass =
          window.AudioContext || window.webkitAudioContext;
        const context = new AudioContextClass();
        const audioBuffer = await context.decodeAudioData(buffer);
        setIsFallback(false);
        if (context.close) context.close();

        const result = buildPeaks(audioBuffer);
        cache.set(src, { peaks: result, duration: audioBuffer.duration });
        if (!cancelled) {
          setPeaks(result);
          setDecodedDuration(audioBuffer.duration);
        }
      } catch (e) {
        console.error("Не удалось построить волну", e);
        if (!cancelled) {
          setPeaks(createFakePeaks(src));
          setIsFallback(true);
          setDecodedDuration(0);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src, providedPeaks]);

  return { peaks, decodedDuration, isFallback };
}
